import React, { useState } from 'react';
import { Card, Button, Alert, Spinner, Modal, Toast } from 'react-bootstrap';
import { Download, AlertCircle, Info, CheckCircle2, ShieldCheck } from 'lucide-react';
import useAuthHeaders from '../../hooks/useAuthHeaders';
import RequestViewer from './RequestViewer';

const API_URL_V2 = process.env.REACT_APP_API_URL_V2;

/**
 * Tab de Opinión de Cumplimiento.
 *   FIEL → POST /ocfiel  (FormData: llavePrivada, Certificado, Contrasena)
 *   CIEC → GET  /oc      (headers: Rfc, Secret)
 * Respuesta: blob PDF → descarga automática.
 */
const OpinionCumplimientoTab = ({ credentials, isValid }) => {
  const { getToken } = useAuthHeaders();
  const isCiec = credentials.authMode === 'ciec';

  const [loading, setLoading]         = useState(false);
  const [showToast, setShowToast]     = useState(false);
  const [errorMsg, setErrorMsg]       = useState('');
  const [showError, setShowError]     = useState(false);
  const [lastRequest, setLastRequest] = useState(null);

  const [vaultSaving, setVaultSaving] = useState(false);
  const [vaultSaveOk, setVaultSaveOk] = useState(false);
  const [vaultSaveErr, setVaultSaveErr] = useState('');

  const vaultRegistered = credentials.signingKeyStatus?.isRegistered === true;

  const buildFielFormData = () => {
    const fd = new FormData();
    if (!vaultRegistered) {
      fd.append('llavePrivada', credentials.privateKeyFile);
      fd.append('Certificado',  credentials.certFile);
      fd.append('Contrasena',   credentials.fielPassword);
    }
    return fd;
  };

  const downloadPdf = (blob) => {
    const url  = URL.createObjectURL(blob);
    const link = Object.assign(document.createElement('a'), {
      href: url,
      download: `opinion-cumplimiento-${credentials.rfc}-${new Date().toISOString().slice(0,10)}.pdf`,
    });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setLoading(true);

    try {
      const token    = await getToken();
      const headers  = { 'Authorization': `Bearer ${token}`, 'Rfc': credentials.rfc };
      if (isCiec) headers['Secret'] = credentials.secret;

      let res;
      if (isCiec) {
        // CIEC: GET sin body
        res = await fetch(`${API_URL_V2}/Consultar/oc`, { method: 'GET', headers });
      } else {
        // FIEL: POST con archivos
        res = await fetch(`${API_URL_V2}/Consultar/ocfiel`, { method: 'POST', headers, body: buildFielFormData() });
      }

      if (!res.ok) {
        const ct  = res.headers.get('content-type') || '';
        const msg = ct.includes('application/json')
          ? ((await res.json()).errorMessage || `Error ${res.status}`)
          : (await res.text() || `Error ${res.status}`);
        throw new Error(msg);
      }

      const blob = await res.blob();
      downloadPdf(blob);
      setShowToast(true);

      const endpoint = isCiec ? 'oc' : 'ocfiel';
      setLastRequest({
        method:      isCiec ? 'GET' : 'POST',
        baseUrl:     `${process.env.REACT_APP_API_URL_V2}/Consultar/${endpoint}`,
        queryParams: null,
        headers: {
          'Rfc': credentials.rfc,
          ...(isCiec ? { 'Secret': credentials.secret } : {}),
        },
        formFields: (isCiec || vaultRegistered) ? null : [
          { name: 'llavePrivada', type: 'file', fileName: credentials.privateKeyFile?.name || 'llave.key', varName: 'llavePrivadaFile' },
          { name: 'Certificado',  type: 'file', fileName: credentials.certFile?.name || 'certificado.cer', varName: 'certificadoFile' },
          { name: 'Contrasena',   type: 'text', value: '{contrasena_fiel}' },
        ],
      });

      // Guardar FIEL en vault si el usuario lo solicitó
      if (!isCiec && !vaultRegistered && credentials.saveToVault && credentials.onStoreSigningKey) {
        setVaultSaving(true);
        setVaultSaveOk(false);
        setVaultSaveErr('');
        const result = await credentials.onStoreSigningKey();
        setVaultSaving(false);
        if (result.success) {
          setVaultSaveOk(true);
        } else {
          setVaultSaveErr(result.error || 'No se pudo guardar la FIEL en Key Vault.');
        }
      }

    } catch (err) {
      setErrorMsg(err.message || 'Error al obtener la Opinión de Cumplimiento.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card className="tab-section-card border-0 shadow-sm">
        <Card.Body>
          <Alert variant="info" className="d-flex align-items-start gap-2 mb-4">
            <Info size={18} className="flex-shrink-0 mt-1" />
            <div>
              <strong>Opinión de Cumplimiento Fiscal</strong>
              <div className="mt-1 small">
                Genera el documento oficial del SAT que certifica el estado de cumplimiento de
                obligaciones fiscales del RFC seleccionado. Se descargará como PDF automáticamente.
              </div>
            </div>
          </Alert>

          {!isValid && (
            <Alert variant="warning" className="d-flex align-items-center py-2 mb-3">
              <AlertCircle size={16} className="me-2 flex-shrink-0" />
              <small>Completa las credenciales de arriba para habilitar la descarga.</small>
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Button type="submit" className="btn-gradient" disabled={!isValid || loading}>
              {loading
                ? <><Spinner animation="border" size="sm" className="me-2" />Generando PDF…</>
                : <><Download size={18} />Generar Opinión de Cumplimiento</>
              }
            </Button>
          </form>

          <RequestViewer request={lastRequest} />
        </Card.Body>
      </Card>

      <Toast show={showToast} onClose={() => setShowToast(false)} delay={4000} autohide
        className="custom-toast toast-success">
        <Toast.Header>
          <CheckCircle2 size={16} className="me-2 text-success" />
          <strong className="me-auto">PDF generado</strong>
        </Toast.Header>
        <Toast.Body>La Opinión de Cumplimiento se descargó correctamente.</Toast.Body>
      </Toast>

      <Toast show={vaultSaving} className="custom-toast"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header closeButton={false}>
          <Spinner animation="border" size="sm" className="me-2" />
          <strong className="me-auto">Guardando en Key Vault…</strong>
        </Toast.Header>
      </Toast>

      <Toast show={vaultSaveOk} onClose={() => setVaultSaveOk(false)} delay={5000} autohide
        className="custom-toast toast-success"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header>
          <ShieldCheck size={14} className="me-2 text-success" />
          <strong className="me-auto">FIEL guardada en Key Vault</strong>
        </Toast.Header>
        <Toast.Body>Ya no necesitarás subir los archivos FIEL para este RFC.</Toast.Body>
      </Toast>

      <Toast show={!!vaultSaveErr} onClose={() => setVaultSaveErr('')} delay={7000} autohide
        className="custom-toast toast-error"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header>
          <AlertCircle size={14} className="me-2 text-danger" />
          <strong className="me-auto">No se pudo guardar en Key Vault</strong>
        </Toast.Header>
        <Toast.Body style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{vaultSaveErr}</Toast.Body>
      </Toast>

      <Modal show={showError} onHide={() => setShowError(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '3px solid #ff6b6b' }}>
          <Modal.Title className="d-flex align-items-center gap-2" style={{ color: '#1a1f3a' }}>
            <AlertCircle size={20} color="#ff6b6b" />Error al generar el PDF
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0" style={{ color: '#4a5568' }}>{errorMsg}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowError(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OpinionCumplimientoTab;
